// Achievements: tiered badges (bronze → silver → gold), one-off milestones and secret ones.
// Pin up to three to show them off on your profile.
import { api } from '../api.ts';
import { useGame, fmt, ago } from '../state.tsx';
import { Loading, useLoad, Panel } from '../components/ui.tsx';

const TIER_LABEL: Record<string, string> = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold' };

export function Medal({ tier, size = 34, off = false }: { tier: string | null; size?: number; off?: boolean }) {
  return <div className={`badge-medal ${tier ?? 'star'} ${off ? 'off' : ''}`} style={{ width: size, height: size, fontSize: size * 0.5 }}>{off ? '?' : '★'}</div>;
}

export function AchievementsPage({ tab }: { tab?: string }) {
  return tab === 'titles' ? <TitlesPage /> : <BadgesPage />;
}

function BadgesPage() {
  const { byId, me, content, act } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/achievements'), [me?.favorPoints]);
  if (!data) return <Loading />;
  const list = data.achievements as any[];
  const byAch = new Map(list.map((a) => [a.id, a]));
  const earned = list.filter((a) => a.earnedAt).length;
  const pinned: string[] = list.filter((a) => a.pinned).map((a) => a.id);
  const togglePin = async (id: string) => {
    const next = pinned.includes(id) ? pinned.filter((x) => x !== id) : [...pinned, id];
    if (next.length > 3) return;
    await act('/api/achievements/pin', { ids: next });
    reload();
  };
  const PinLink = ({ id }: { id: string }) => {
    const a = byAch.get(id);
    if (!a?.earnedAt) return null;
    const on = pinned.includes(id);
    return <a className={`pin ${on ? 'on' : ''}`} title={on ? 'Unpin from your profile' : pinned.length >= 3 ? 'You can pin 3. Unpin one first.' : 'Pin to your profile'} onClick={() => (on || pinned.length < 3) && togglePin(id)}>{on ? '● pinned' : '○ pin'}</a>;
  };

  const groups = [...new Set(content.badgeFamilies.map((f: any) => f.group))] as string[];
  const milestones = content.achievements.filter((a: any) => !a.family && !a.hidden);
  const secrets = content.achievements.filter((a: any) => a.hidden);

  return (
    <div>
      <h1 className="page-title">Achievements</h1>
      <div className="subnav"><a className="active" href="#/achievements">Badges</a><a href="#/achievements/titles">Titles</a></div>
      <p className="muted">
        You have earned <b className="gold">{earned}</b> of {list.length}. Most badges come in <span className="t-bronze">bronze</span>, <span className="t-silver">silver</span> and <span className="t-gold">gold</span>,
        and each one pays favor points. Pin up to three to show them on your profile ({pinned.length}/3 pinned). Some achievements are secret until someone earns them.
      </p>
      {groups.map((g) => (
        <Panel key={g} title={g}>
          <div className="badge-grid">
            {content.badgeFamilies.filter((f: any) => f.group === g).map((f: any) => {
              const tiers = content.achievements.filter((a: any) => a.family === f.id);
              const got = tiers.filter((t: any) => byAch.get(t.id)?.earnedAt);
              const top = got[got.length - 1];
              const next = tiers.find((t: any) => !byAch.get(t.id)?.earnedAt);
              const prog = next ? byAch.get(next.id) : null;
              return (
                <div key={f.id} className={`badge-card ${top ? '' : 'none'}`}>
                  <Medal tier={top?.tier ?? null} off={!top} />
                  <div className="grow">
                    <div className="job-name">{top ? top.name : f.name} {top && <span className={`t-${top.tier} small`}>{TIER_LABEL[top.tier]}</span>}</div>
                    {next ? (
                      <>
                        <div className="small">Next: <b>{next.name}</b> <span className={`t-${next.tier}`}>({TIER_LABEL[next.tier]})</span>: {next.description}</div>
                        <div className="progress" style={{ marginTop: 3 }}><div style={{ width: `${Math.min(100, (prog.progress / prog.goal) * 100)}%` }} /></div>
                        <div className="small muted">{fmt(prog.progress)} / {fmt(prog.goal)} · +{next.favor} favor</div>
                      </>
                    ) : <div className="small good">All three tiers earned!</div>}
                    <div className="tier-dots">
                      {tiers.map((t: any) => {
                        const a = byAch.get(t.id);
                        return (
                          <span key={t.id} className={`tier-dot t-${t.tier} ${a?.earnedAt ? 'got' : ''}`} title={`${TIER_LABEL[t.tier]}: ${t.name}. ${t.description}${a?.earnedAt ? ` Earned ${ago(a.earnedAt)}.` : ''}`}>
                            {TIER_LABEL[t.tier]}{a?.earnedAt && <PinLink id={t.id} />}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      ))}
      <Panel title="Milestones">
        <div className="badge-grid">
          {milestones.map((m: any) => {
            const a = byAch.get(m.id);
            return (
              <div key={m.id} className={`badge-card ${a?.earnedAt ? '' : 'none'}`}>
                <Medal tier={null} off={!a?.earnedAt} />
                <div className="grow">
                  <div className="job-name">{m.name} <PinLink id={m.id} /></div>
                  <div className="small">{m.description}</div>
                  {a?.earnedAt ? <div className="small good">Earned {ago(a.earnedAt)} · +{m.favor} favor</div>
                    : <div className="small muted">{fmt(a?.progress ?? 0)} / {fmt(a?.goal ?? m.goal)} · +{m.favor} favor</div>}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
      <Panel title={`Secret achievements (${secrets.filter((s: any) => byAch.get(s.id)?.earnedAt).length} of ${secrets.length} found)`}>
        <div className="badge-grid">
          {secrets.map((s: any) => {
            const a = byAch.get(s.id);
            return a?.earnedAt ? (
              <div key={s.id} className="badge-card secret found">
                <Medal tier="secret" />
                <div className="grow">
                  <div className="job-name">{a.name} <PinLink id={s.id} /></div>
                  <div className="small">{a.description}</div>
                  <div className="small good">Found {ago(a.earnedAt)}</div>
                </div>
              </div>
            ) : (
              <div key={s.id} className="badge-card secret none">
                <Medal tier={null} off />
                <div className="grow"><div className="job-name">???</div><div className="small dim">Keep playing. Something unusual will unlock it.</div></div>
              </div>
            );
          })}
        </div>
      </Panel>
      <div className="small dim">{byId.achievements.size} achievements in all.</div>
    </div>
  );
}

/** v0.8: every title you can earn, and the ones you have. */
function TitlesPage() {
  const { me } = useGame();
  const { data } = useLoad(() => api.get('/api/titles'), [me?.newTitles?.length]);
  if (!data) return <Loading />;
  const got = data.titles.filter((t: any) => t.earnedAt).length;
  return (
    <div>
      <h1 className="page-title">Achievements</h1>
      <div className="subnav"><a href="#/achievements">Badges</a><a className="active" href="#/achievements/titles">Titles</a></div>
      <p className="muted">Titles are earned by what you do. You have <b className="gold">{got}</b> of {data.titles.length}. Wear one under your name from <a href="#/customize">Customize</a>.</p>
      <div className="title-grid">
        {data.titles.map((t: any) => (
          <div key={t.id} className={`title-card ${t.earnedAt ? 'got' : ''}`}>
            <div className="title-name">{t.earnedAt ? `“${t.name}”` : t.name}</div>
            <div className="small">{t.how}</div>
            {t.earnedAt ? <div className="small good">Earned</div> : <div className="small"><span className="locked-stamp">Locked</span></div>}
          </div>
        ))}
      </div>
    </div>
  );
}
